'use client';

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../domain/api';
import { uploadGallery } from '../../shared/upload';

const EnhancedGalleryForm = () => {
  const [description, setDescription] = useState('');
  const [images, setImages] = useState([]);
  const [author, setAuthor] = useState('Luhaif Digitech');
  const [isDragOver, setIsDragOver] = useState(false);

  const mutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/gallery', data);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Gallery entry added successfully!');
      setDescription('');
      setImages([]);
      setAuthor('Luhaif Digitech');
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || 'Failed to save gallery entry'
      );
    },
  });

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith('image/')
    );
    setImages((prev) => [...prev, ...files]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setImages((prev) => [...prev, ...files]);
  };

  const handleRemoveImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    const uploaded = await uploadGallery(images);
    mutation.mutate({
      description,
      image: uploaded,
      author,
    });
  };

  return (
    <>
      <div className='enhanced-gallery-form-container'>
        <div className='enhanced-gallery-form-wrapper'>
          <div className='enhanced-gallery-form-card'>
            {/* Header */}
            <div className='enhanced-form-header'>
              <div className='enhanced-header-icon'>
                <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                  <path
                    d='M4 16L8.586 11.414C9.367 10.633 10.633 10.633 11.414 11.414L16 16M14 14L15.586 12.414C16.367 11.633 17.633 11.633 18.414 12.414L20 14M14 8H14.01M6 20H18C19.105 20 20 19.105 20 18V6C20 4.895 19.105 4 18 4H6C4.895 4 4 4.895 4 6V18C4 19.105 4.895 20 6 20Z'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <div>
                <h2 className='enhanced-form-title'>Add New Gallery Entry</h2>
                <p className='enhanced-form-subtitle'>
                  Share your moments with the team
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className='enhanced-gallery-form'>
              {/* Description Field */}
              <div className='enhanced-form-group'>
                <label className='enhanced-form-label'>
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M4 6H20M4 12H20M4 18H16'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  Description
                </label>
                <textarea
                  className='enhanced-form-textarea'
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  rows={4}
                  placeholder='Tell us about this gallery entry...'
                />
              </div>

              {/* Image Upload Area */}
              <div className='enhanced-form-group'>
                <label className='enhanced-form-label'>
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M21 15V19C21 20.105 20.105 21 19 21H5C3.895 21 3 20.105 3 19V15M17 8L12 3M12 3L7 8M12 3V15'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  Upload Images
                </label>

                <div
                  className={`enhanced-dropzone ${
                    isDragOver ? 'drag-over' : ''
                  }`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() =>
                    document.getElementById('gallery-file-input').click()
                  }
                >
                  <div className='enhanced-dropzone-content'>
                    <div className='enhanced-dropzone-icon'>
                      <svg
                        width='48'
                        height='48'
                        viewBox='0 0 24 24'
                        fill='none'
                      >
                        <path
                          d='M21 15V19C21 20.105 20.105 21 19 21H5C3.895 21 3 20.105 3 19V15M17 8L12 3M12 3L7 8M12 3V15'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </div>
                    <h4 className='enhanced-dropzone-title'>
                      {isDragOver ? 'Drop images here' : 'Upload Images'}
                    </h4>
                    <p className='enhanced-dropzone-text'>
                      Drag & drop your images here, or{' '}
                      <span className='enhanced-browse-link'>browse files</span>
                    </p>
                    <p className='enhanced-dropzone-hint'>
                      Supports: JPG, PNG, GIF up to 10MB each
                    </p>
                  </div>

                  <input
                    id='gallery-file-input'
                    type='file'
                    multiple
                    accept='image/*'
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </div>

                {/* Image Preview Grid */}
                {images.length > 0 && (
                  <div className='enhanced-image-preview-section'>
                    <h5 className='enhanced-preview-title'>
                      Selected Images ({images.length})
                    </h5>
                    <div className='enhanced-image-grid'>
                      {images.map((img, idx) => (
                        <div className='enhanced-image-card' key={idx}>
                          <div className='enhanced-image-wrapper'>
                            <img
                              src={
                                URL.createObjectURL(img) || '/placeholder.svg'
                              }
                              alt={`preview-${idx}`}
                              className='enhanced-preview-image'
                            />
                            <div className='enhanced-image-overlay'>
                              <button
                                type='button'
                                className='enhanced-remove-btn'
                                onClick={() => handleRemoveImage(idx)}
                                title='Remove image'
                              >
                                <svg
                                  width='16'
                                  height='16'
                                  viewBox='0 0 24 24'
                                  fill='none'
                                >
                                  <path
                                    d='M18 6L6 18M6 6L18 18'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                          <p className='enhanced-image-name'>{img.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Author Field */}
              <div className='enhanced-form-group'>
                <label className='enhanced-form-label'>
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  Author
                </label>
                <input
                  type='text'
                  className='enhanced-form-input'
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder='Enter author name'
                />
              </div>

              {/* Submit Button */}
              <button
                type='submit'
                className='enhanced-submit-btn'
                disabled={mutation.isLoading}
              >
                {mutation.isLoading ? (
                  <>
                    <div className='enhanced-spinner'></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                      <path
                        d='M12 2L2 7L12 12L22 7L12 2Z'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M2 17L12 22L22 17'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M2 12L12 17L22 12'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                    Add to Gallery
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      <style jsx>{`
        .enhanced-gallery-form-container {
          min-height: 100vh;
          background: linear-gradient(
            135deg,
            #f8fafc 0%,
            #fef2f2 50%,
            #fff1f2 100%
          );
          padding: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .enhanced-gallery-form-wrapper {
          width: 100%;
          max-width: 600px;
        }

        .enhanced-gallery-form-card {
          background: white;
          border-radius: 20px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
            0 10px 10px -5px rgba(0, 0, 0, 0.04);
          overflow: hidden;
        }

        .enhanced-form-header {
          background: linear-gradient(
            135deg,
            #1f2937 0%,
            #374151 30%,
            #dc2626 70%,
            #b91c1c 100%
          );
          color: white;
          padding: 30px;
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .enhanced-header-icon {
          width: 60px;
          height: 60px;
          background: rgba(255, 255, 255, 0.15);
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .enhanced-form-title {
          font-size: 28px;
          font-weight: 700;
          margin: 0 0 5px 0;
        }

        .enhanced-form-subtitle {
          font-size: 16px;
          opacity: 0.9;
          margin: 0;
        }

        .enhanced-gallery-form {
          padding: 40px;
        }

        .enhanced-form-group {
          margin-bottom: 30px;
        }

        .enhanced-form-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 12px;
          font-size: 15px;
        }

        .enhanced-form-input,
        .enhanced-form-textarea {
          width: 100%;
          padding: 14px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          font-size: 15px;
          transition: all 0.2s ease;
          background: #fafafa;
        }

        .enhanced-form-input:focus,
        .enhanced-form-textarea:focus {
          outline: none;
          border-color: #dc2626;
          background: white;
          box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
        }

        .enhanced-form-textarea {
          resize: vertical;
          min-height: 100px;
        }

        .enhanced-dropzone {
          border: 3px dashed #d1d5db;
          border-radius: 16px;
          background: #fafafa;
          padding: 40px 20px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .enhanced-dropzone:hover,
        .enhanced-dropzone.drag-over {
          border-color: #dc2626;
          background: #fef2f2;
          transform: translateY(-2px);
        }

        .enhanced-dropzone-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .enhanced-dropzone-icon {
          color: #9ca3af;
          transition: color 0.2s ease;
        }

        .enhanced-dropzone:hover .enhanced-dropzone-icon,
        .enhanced-dropzone.drag-over .enhanced-dropzone-icon {
          color: #dc2626;
        }

        .enhanced-dropzone-title {
          font-size: 20px;
          font-weight: 600;
          color: #374151;
          margin: 0;
        }

        .enhanced-dropzone-text {
          font-size: 15px;
          color: #6b7280;
          margin: 0;
        }

        .enhanced-browse-link {
          color: #dc2626;
          font-weight: 600;
          text-decoration: underline;
        }

        .enhanced-dropzone-hint {
          font-size: 13px;
          color: #9ca3af;
          margin: 0;
        }

        .enhanced-image-preview-section {
          margin-top: 25px;
          padding: 20px;
          background: #f9fafb;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
        }

        .enhanced-preview-title {
          font-size: 16px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 20px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .enhanced-image-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
          gap: 16px;
        }

        .enhanced-image-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          transition: transform 0.2s ease;
        }

        .enhanced-image-card:hover {
          transform: translateY(-2px);
        }

        .enhanced-image-wrapper {
          position: relative;
          aspect-ratio: 1;
          overflow: hidden;
        }

        .enhanced-preview-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .enhanced-image-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .enhanced-image-wrapper:hover .enhanced-image-overlay {
          opacity: 1;
        }

        .enhanced-remove-btn {
          background: #dc2626;
          color: white;
          border: none;
          border-radius: 50%;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .enhanced-remove-btn:hover {
          background: #b91c1c;
          transform: scale(1.1);
        }

        .enhanced-image-name {
          padding: 12px;
          font-size: 12px;
          color: #6b7280;
          margin: 0;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
        }

        .enhanced-submit-btn {
          width: 100%;
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 16px 24px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
        }

        .enhanced-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(220, 38, 38, 0.4);
        }

        .enhanced-submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .enhanced-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 768px) {
          .enhanced-gallery-form-container {
            padding: 10px;
          }

          .enhanced-form-header {
            padding: 20px;
            flex-direction: column;
            text-align: center;
          }

          .enhanced-gallery-form {
            padding: 20px;
          }

          .enhanced-image-grid {
            grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
            gap: 12px;
          }
        }
      `}</style>
    </>
  );
};

export default EnhancedGalleryForm;
